import React, { useCallback, useEffect, useState } from 'react';
import { getAnalyticsOverview } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Info, RefreshCw } from 'lucide-react';
import { StatCards } from './analytics/StatCards';
import { DailyChart, HourlyChart, SourcesChart, DevicesChart } from './analytics/Charts';
import { DataTable } from './analytics/DataTable';

const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });
const shift = (iso, days) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
};
const PRESETS = {
  today: () => [istToday(), istToday()],
  yesterday: () => [shift(istToday(), -1), shift(istToday(), -1)],
  last7: () => [shift(istToday(), -6), istToday()],
  last30: () => [shift(istToday(), -29), istToday()],
  month: () => [istToday().slice(0, 8) + '01', istToday()],
};
const PRESET_LABELS = { today: 'Bugün', yesterday: 'Dün', last7: 'Son 7 gün', last30: 'Son 30 gün', month: 'Bu ay', custom: 'Özel aralık' };

const PAGE_COLS = [
  { key: 'title', label: 'Sayfa' }, { key: 'path', label: 'Adres' }, { key: 'tour_type', label: 'Kategori' },
  { key: 'users', label: 'Kullanıcı', numeric: true }, { key: 'views', label: 'Görüntüleme', numeric: true },
  { key: 'avg_engagement_sec', label: 'Ort. etkileşim (sn)', numeric: true }, { key: 'booking_actions', label: 'Rezervasyon hareketi', numeric: true },
];
const FUNNEL_COLS = [
  { key: 'title', label: 'Tur' }, { key: 'tour_type', label: 'Kategori' }, { key: 'views', label: 'Görüntüleme', numeric: true },
  { key: 'users', label: 'Kullanıcı', numeric: true }, { key: 'booking_starts', label: 'Rezervasyona başlama', numeric: true },
  { key: 'bookings_completed', label: 'Tamamlanan rezervasyon', numeric: true },
  { key: 'whatsapp_clicks', label: 'WhatsApp (iletişim)', numeric: true }, { key: 'phone_clicks', label: 'Telefon (iletişim)', numeric: true },
];
const SOURCE_COLS = [
  { key: 'channel', label: 'Kanal' }, { key: 'source_medium', label: 'Kaynak / Araç' }, { key: 'campaign', label: 'Kampanya' },
  { key: 'landing_page', label: 'İlk giriş sayfası' }, { key: 'sessions', label: 'Oturum', numeric: true }, { key: 'users', label: 'Kullanıcı', numeric: true },
];

export default function AnalyticsDashboard() {
  const [preset, setPreset] = useState('last7');
  const [range, setRange] = useState(PRESETS.last7());
  const [day, setDay] = useState(istToday());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAnalyticsOverview({ start: range[0], end: range[1], day });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Veriler alınamadı');
    } finally {
      setLoading(false);
    }
  }, [range, day]);

  useEffect(() => { load(); }, [load]);

  const choosePreset = (key) => {
    setPreset(key);
    if (PRESETS[key]) {
      const r = PRESETS[key]();
      setRange(r);
      setDay(r[1]);
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900">Ziyaretçi ve Satış İstatistikleri</h2>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="analytics-refresh"><RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Yenile</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="analytics-presets">
        {Object.entries(PRESET_LABELS).map(([key, label]) => (
          <button key={key} onClick={() => choosePreset(key)} data-testid={`analytics-preset-${key}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${preset === key ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-gray-200 hover:border-orange-300'}`}>
            {label}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="analytics-custom-start" />
            <span>–</span>
            <input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="analytics-custom-end" />
          </div>
        )}
      </div>

      <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-900 flex gap-3" data-testid="analytics-notice">
        <Info size={18} className="flex-shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p><strong>Kaynak:</strong> {data?.data_source || 'Site içi ölçüm'} · <strong>Son güncelleme:</strong> {data ? new Date(data.generated_at).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : '–'} · <strong>Ölçüm başlangıcı:</strong> {data?.measurement_started_at ? new Date(data.measurement_started_at).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : 'Henüz veri yok'}</p>
          <p>Kullanıcı sayısı cihaz, tarayıcı çerezi ve izinlere bağlı yaklaşık bir değerdir; aynı kişi farklı cihazdan gelirse ayrı sayılabilir. Çerez iznini reddeden ziyaretçiler ve yönetici/test trafiği sayılmaz. Bu tarihten önceki dönemler ölçülmemiştir.</p>
          <p>Tamamlanan rezervasyonlar sunucudaki gerçek rezervasyon kayıtlarından (iptal ve test kayıtları hariç) sayılır. WhatsApp ve telefon tıklamaları satış değil, iletişim hareketidir. Ülke/şehir bilgisi bu ölçümde toplanmaz; Google Analytics bağlandığında orada görülebilir.</p>
        </div>
      </div>

      {error && <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700" data-testid="analytics-error">Hata: {error}</div>}

      {data && (
        <>
          <StatCards summary={data.summary} active={data.active_last_30_min} />
          <p className="text-xs text-gray-500">Karşılaştırma: seçilen dönemle aynı uzunluktaki bir önceki dönem.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DailyChart data={data.daily} />
            <HourlyChart data={data.hourly} day={day} onDayChange={setDay} />
            <SourcesChart data={data.sources} />
            <DevicesChart data={data.devices} />
          </div>
          <DataTable title="Tur bazında akış: görüntüleme → rezervasyona başlama → tamamlanan" columns={FUNNEL_COLS} rows={data.tour_funnel} testid="table-tour-funnel" searchKeys={['title']} typeFilter filename="tur-akisi.csv" />
          <DataTable title="Sayfa performansı" columns={PAGE_COLS} rows={data.pages} testid="table-pages" searchKeys={['title', 'path']} typeFilter filename="sayfa-performansi.csv" />
          <DataTable title="Trafik kaynakları, kampanyalar ve giriş sayfaları" columns={SOURCE_COLS} rows={data.source_detail} testid="table-sources" searchKeys={['channel', 'source_medium', 'campaign', 'landing_page']} filename="trafik-kaynaklari.csv" />
          <div className="rounded-2xl bg-white border border-dashed border-gray-300 p-6" data-testid="gsc-section">
            <h3 className="font-bold text-gray-900">Google Search Console – Arama performansı</h3>
            <p className="text-sm text-gray-600 mt-2"><strong className="text-amber-600">Bağlantı gerekli.</strong> Arama kelimeleri, gösterim, tıklama, tıklama oranı ve ortalama konum; Search Console hesabınız doğrulanıp sunucuya Google servis hesabı yetkisi verildiğinde burada gösterilecek. Search Console verileri genellikle 2–3 gün gecikmelidir ve site içi ölçümle birebir eşleşmez.</p>
          </div>
        </>
      )}
    </div>
  );
}
